import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Layers, Loader2, RefreshCw, AlertTriangle,
  TrendingUp, TrendingDown,
} from "lucide-react";

/**
 * Stage 5: Cross-tenant rollup dashboard. Reads `xero_tenant_snapshots`
 * via `GET /api/integrations/xero/rollup`. Manual refresh via
 * `POST /rollup/refresh` (also runs nightly via APScheduler at 06:50
 * Sydney as job `xero_rollup_sync`).
 */
export default function XeroRollupPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/xero/rollup");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load rollup");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: result } = await api.post(
        "/integrations/xero/rollup/refresh", {}, { timeout: 180000 });
      toast.success(
        `Synced ${result.synced ?? 0} of ${result.tenants ?? 0} tenants`);
      if (result.errors?.length) {
        toast.error(`${result.errors.length} tenant(s) had errors`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const [refreshingTenants, setRefreshingTenants] = useState(false);
  const refreshTenants = async () => {
    setRefreshingTenants(true);
    try {
      const { data } = await api.post(
        "/integrations/xero/tenants/refresh", {}, { timeout: 30000 });
      if (data.added_count > 0) {
        toast.success(
          `Found ${data.added_count} new organisation${data.added_count === 1 ? "" : "s"}: ${data.added.map((t) => t.tenant_name).join(", ")}`);
      } else if (data.removed_count > 0) {
        toast.warning(
          `${data.removed_count} organisation${data.removed_count === 1 ? "" : "s"} no longer authorised — removed from the list.`);
      } else {
        toast.info(`No changes · ${data.tenant_count} organisation(s) authorised`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh organisations");
    } finally { setRefreshingTenants(false); }
  };

  const totals = data?.totals || {};
  const tenants = data?.tenants || [];
  const missing = data?.missing || [];

  return (
    <div className="p-6 max-w-7xl" data-testid="xero-rollup-page">
      <Link to="/integrations/xero"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="xero-rollup-back">
        <ArrowLeft className="h-3 w-3" /> Back to Xero
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#13B5EA]/10">
          <Layers className="h-6 w-6 text-[#13B5EA]" />
        </div>
        <div className="flex-1">
          <div className="label-overline">XERO · CROSS-TENANT ROLLUP</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            All organisations
          </h1>
          {data?.last_synced_at && (
            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
              Last synced: {new Date(data.last_synced_at).toLocaleString()}
              <span className="ml-2 text-zinc-400">
                · cached snapshot · nightly auto-refresh @ 06:50 Sydney
              </span>
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={refreshTenants} disabled={refreshingTenants}
          data-testid="xero-rollup-refresh-tenants">
          {refreshingTenants ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Layers className="h-3.5 w-3.5 mr-1.5" />
          )}
          {refreshingTenants ? "Looking…" : "Refresh organisations"}
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={refresh} disabled={refreshing}
          data-testid="xero-rollup-refresh">
          {refreshing ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          )}
          {refreshing ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </CardContent>
        </Card>
      ) : !data?.connected ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-8 text-center text-sm italic text-zinc-500">
            Connect Xero from the integrations page to enable cross-tenant
            rollup.
          </CardContent>
        </Card>
      ) : tenants.length === 0 ? (
        <Card className="rounded-sm border-amber-200 bg-amber-50">
          <CardContent className="p-6 text-sm text-amber-900">
            <div className="font-display font-bold mb-1">Cache is empty.</div>
            <div className="text-xs">
              {missing.length} tenant(s) authorised but never synced. Click
              <span className="font-mono mx-1">Sync now</span>
              to run the first rollup — it&apos;ll then refresh automatically
              every night at 06:50 Sydney.
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Grand totals ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4"
            data-testid="xero-rollup-totals">
            <KpiTile label="Revenue (MTD)" value={fmtMoney(totals.revenue_mtd)}
              accent="text-emerald-700" />
            <KpiTile label="Net Profit (MTD)" value={fmtMoney(totals.net_profit_mtd)}
              accent={(totals.net_profit_mtd ?? 0) >= 0 ? "text-emerald-700" : "text-red-700"}
              icon={(totals.net_profit_mtd ?? 0) >= 0 ? TrendingUp : TrendingDown} />
            <KpiTile label="AR outstanding" value={fmtMoney(totals.ar_outstanding)}
              accent="text-amber-700" />
            <KpiTile label="AR overdue" value={fmtMoney(totals.ar_overdue)}
              accent={(totals.ar_overdue ?? 0) > 0 ? "text-red-700" : ""} />
            <KpiTile label="AP outstanding" value={fmtMoney(totals.ap_outstanding)}
              accent="text-zinc-700" />
            <KpiTile label="AP overdue" value={fmtMoney(totals.ap_overdue)}
              accent={(totals.ap_overdue ?? 0) > 0 ? "text-red-700" : ""} />
          </div>

          {missing.length > 0 && (
            <Card className="rounded-sm border-amber-200 bg-amber-50 mb-4">
              <CardContent className="p-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold">{missing.length}</span> tenant(s)
                  authorised but missing a cached snapshot:{" "}
                  {missing.map((m) => m.tenant_name).join(", ")}. Click Sync now.
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Per-tenant table ── */}
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-0">
              <div className="grid grid-cols-[1.4fr_110px_110px_110px_110px_110px_110px_70px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
                <span>Organisation</span>
                <span className="text-right">Revenue MTD</span>
                <span className="text-right">Net Profit MTD</span>
                <span className="text-right">AR outstanding</span>
                <span className="text-right">AR overdue</span>
                <span className="text-right">AP outstanding</span>
                <span className="text-right">AP overdue</span>
                <span className="text-right">Actions</span>
              </div>
              {tenants.map((t) => (
                <div key={t.tenant_id}
                  className="grid grid-cols-[1.4fr_110px_110px_110px_110px_110px_110px_70px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50"
                  data-testid={`xero-rollup-tenant-${t.tenant_id}`}>
                  <span className="truncate">
                    <div className="font-semibold">{t.tenant_name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">
                      {t.tenant_type || "ORGANISATION"} · synced {fmtRel(t.synced_at)}
                      {(t.errors || []).length > 0 && (
                        <span className="ml-2 text-red-600">
                          · {t.errors.length} error(s)
                        </span>
                      )}
                    </div>
                  </span>
                  <span className="font-mono text-right text-emerald-700">
                    {fmtMoney(t.revenue_mtd)}
                  </span>
                  <span className={`font-mono text-right ${
                    (t.net_profit_mtd ?? 0) >= 0 ? "text-emerald-700" : "text-red-700 font-semibold"
                  }`}>
                    {fmtMoney(t.net_profit_mtd)}
                  </span>
                  <span className={`font-mono text-right ${
                    Number(t.ar_outstanding) > 0 ? "text-amber-700" : "text-zinc-400"
                  }`}>
                    {fmtMoney(t.ar_outstanding)}
                  </span>
                  <span className={`font-mono text-right ${
                    Number(t.ar_overdue) > 0 ? "text-red-700 font-semibold" : "text-zinc-400"
                  }`}>
                    {fmtMoney(t.ar_overdue)}
                  </span>
                  <span className={`font-mono text-right ${
                    Number(t.ap_outstanding) > 0 ? "text-amber-700" : "text-zinc-400"
                  }`}>
                    {fmtMoney(t.ap_outstanding)}
                  </span>
                  <span className={`font-mono text-right ${
                    Number(t.ap_overdue) > 0 ? "text-red-700 font-semibold" : "text-zinc-400"
                  }`}>
                    {fmtMoney(t.ap_overdue)}
                  </span>
                  <span className="text-right">
                    <Link to={`/integrations/xero/tenants/${t.tenant_id}/reports`}
                      data-testid={`xero-rollup-open-${t.tenant_id}`}>
                      <Badge variant="outline"
                        className="rounded-sm text-[9px] cursor-pointer hover:bg-[#13B5EA]/10 hover:border-[#13B5EA]">
                        Open →
                      </Badge>
                    </Link>
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, accent, icon: Icon }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white">
      <div className="label-overline text-[9px] flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>
        {value}
      </div>
    </div>
  );
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function fmtRel(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
